import { AddaDynamicLoaderService } from '@addatech-angular/basic';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserIdleService } from 'angular-user-idle';
import { DxDropDownButtonModule, DxScrollViewModule } from 'devextreme-angular';
import config from 'devextreme/core/config';
import * as dxLocale from 'devextreme/localization';
import moment from 'moment';
import { Subscription, filter } from 'rxjs';
import { LoginComponent } from './core/auth/login/login.component';
import { ROOT_NAV_ICONS } from './core/constants/root-nav-icons.const';
import { licenseKey } from './core/licenses/devextreme-key';
import { ExternalResourceExpiry } from './core/models/external-resource-expiry.model';
import { Identifier } from './core/models/storage.model';
import { SysSecuredMenu } from './core/models/sys-secured-menu.model';
import { ActionsService } from './core/services/actions.service';
import { AuthService } from './core/services/auth.service';
import { HttpLoadingIndicatorService } from './core/services/http-loading-indicator.service';
import { LocalStorageService } from './core/services/local-storage.service';
import { SysTablesService } from './core/services/sys-tables.service';
import { User } from './core/utils/auth.utils';
import { setDefaultDevextremeOptions } from './core/utils/devextreme.utils';
import { UpcomingExpirationComponent } from './features/dashboard/components/upcoming-expiration/upcoming-expiration.component';
import { SettingsHttpService } from './features/settings/services/settings.service';
import { DynamicLoaderService } from './shared/dynamic/services/dynamic-loader.service';
import { SharedPopupModule } from './shared/popup/popup.module';
import { selectUserIdle, setUserIdle } from './store/ui';
config({ licenseKey });

@Component({
  selector: 'app-root',
  standalone: true,
  providers: [],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DxScrollViewModule,
    DxDropDownButtonModule,
    TranslateModule,
    RouterOutlet,
    RouterLink,
    LoginComponent,
    SharedPopupModule,
    UpcomingExpirationComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  moment = moment;

  isHelp = false;
  isLoading: boolean = false;
  isNavOpen = true;
  portalUser?: User;
  title = 'ssics';
  userPhotoUrl = '';
  fullName = '';
  isLoggedIn = true;
  sysSecuredMenus: SysSecuredMenu[] = [];

  refExternalResourceExp: ExternalResourceExpiry[] = [];
  refExternalResourceExpFlag: boolean = false;
  isExternalResExpPopup: boolean = false;
  popupHeading?: string;

  userSecondsIdle?: number;
  idleTimeOutLimit = 6000;
  displayIdleConfirmDialog = false;

  showRefreshPopup = false;

  private idleSubscription: Subscription = new Subscription();
  private subscription: Subscription = new Subscription();
  private jwtHelper = new JwtHelperService();

  constructor(
    private localStorageService: LocalStorageService,
    private translateService: TranslateService,
    private sysTablesHttpService: SysTablesService,
    private ref: ChangeDetectorRef,
    private authService: AuthService,
    private userIdle: UserIdleService,
    private readonly store: Store,
    public viewContRef: ViewContainerRef,
    private addaDynamicLoaderService: AddaDynamicLoaderService,
    private settingsHttpService: SettingsHttpService,
    private actionsService: ActionsService,
    private router: Router,
    private swUpdate: SwUpdate,
    private loadingService: HttpLoadingIndicatorService,
    private dynamicLoaderService: DynamicLoaderService
  ) {
    setDefaultDevextremeOptions();
    this.addaDynamicLoaderService.viewContainerRef = this.viewContRef;
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.displayIdleConfirmDialog) {
      if (event.key === 'Escape') {
        this.restartIdleTimer();
      }
    }
  }

  ngOnInit(): void {
    this.subscribeToSoftwareUpdate();
    this.subscribeToTokenLocalStorage();
    this.setDefaultLang();
    this._setDefaultBackgrounds();
    this.subscribeToRouteChange();
    this.localStorageService.setStorageItem(
      Identifier.TYPE_API_KEY,
      '372992E6-21C6-4A27-80C1-FF9688C91F3A'
    );
    this.subscribeAuthenticatedUserChanged();

    this.loadingService.loading$.subscribe(val => {
      this.isLoading = val;
      this.ref.detectChanges();
    });
  }

  private _setDefaultBackgrounds() {
    this.settingsHttpService.getDefaultBackgroundImages().subscribe({
      next: images => {
        let main: string = '';
        let popup: string = '';
        images.forEach(img => {
          if (img.isMainBackgroundImage) main = img.fileUri;
          if (img.isPopupBackgroundImage) popup = img.fileUri;
        });

        this.actionsService.setMainImage(main ? main : '/../assets/images/bombardier-bg.jpg');
        this.actionsService.setPopupImage(popup ? popup : '/../assets/images/bombardier-bg2.jpg');
      },
    });
  }

  private _getExternalResourceExp() {
    if (!this.refExternalResourceExp.length) {
      this.subscription.add(
        this.settingsHttpService
          .getExternalResourceExpiries()
          .subscribe((response: ExternalResourceExpiry[]) => {
            const today = moment();

            this.refExternalResourceExp = response
              .map(record => {
                const expiry = moment(record.expiryDate);
                const daysLeft = expiry.diff(today, 'days');
                return {
                  ...record,
                  daysLeft: daysLeft,
                };
              })
              .filter(record => record.daysLeft <= 30);

            this.refExternalResourceExpFlag = this.refExternalResourceExp.length > 0;

            this.ref.markForCheck();
          })
      );
    }
  }

  ngOnDestroy(): void {
    localStorage.removeItem(Identifier.TYPE_API_KEY);
    this.subscription.unsubscribe();
    this.idleSubscription.unsubscribe();
  }

  getSysSecuredMenu(): void {
    this.subscription.add(
      this.sysTablesHttpService.getSysSecuredMenuRoots().subscribe(sysSecuredMenu => {
        const visibleMenus =
          JSON.parse(localStorage.getItem(Identifier.TYPE_VISIBLE_MENUS) || '') || [];
        sysSecuredMenu.forEach(menu => {
          const details = ROOT_NAV_ICONS.find(iar => iar.id === menu.id);
          menu.icon = details?.icon;
          menu.route = details?.route;
          if (menu.id === 1000 || menu.id === 1001) {
            menu.isActive =
              visibleMenus && visibleMenus?.length > 0 && visibleMenus?.includes(menu.id);
          }
        });
        this.sysSecuredMenus = sysSecuredMenu;
        this.ref.markForCheck();
      })
    );
  }

  navToggle(): void {
    this.isNavOpen = !this.isNavOpen;
  }

  setLang(lang: string): void {
    this.localStorageService.setStorageItem(LocalStorageService.TYPE_LANGUAGE, lang);
  }

  setDefaultLang() {
    const curLang = 'en-CA';
    this.translateService.setDefaultLang(curLang);
    this.translateService.use(curLang);
    dxLocale.locale(curLang);
  }

  onRouteActivate(): void {
    this.isNavOpen = false;
  }

  onOpenHelpInNewTab() {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/help']));
    window.open(url, '_blank');
  }

  private subscribeToTokenLocalStorage(): void {
    this.subscription.add(
      this.localStorageService.getStorageItem(Identifier.TYPE_TOKEN).subscribe(token => {
        if (token) {
          this.setIdleCheck(60, token); // TODO 60 minutes to be changed to what is configured for system
          if (token && !this.jwtHelper.isTokenExpired(token)) {
            this.isLoggedIn = true;
          } else {
            this.isLoggedIn = false;
          }
        } else {
          this.flushIdleTimer();
          this.isLoggedIn = false;
        }
      })
    );
  }

  private subscribeToSoftwareUpdate(): void {
    if (this.swUpdate.isEnabled) {
      this.subscription.add(
        this.swUpdate.versionUpdates
          .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
          .subscribe(() => {
            this.showRefreshPopup = true;
            this.ref.markForCheck();
          })
      );
    }
  }

  private setIdleCheck(inactivityInMinutes: number, token: string): void {
    this.flushIdleTimer();
    this.userIdle.setConfigValues({
      idle: inactivityInMinutes * 60,
      timeout: this.idleTimeOutLimit,
      ping: 120,
    });
    if (token && !this.jwtHelper.isTokenExpired(token)) {
      this.startWatching();
      this.idleSubscription.add(
        this.store
          .select(selectUserIdle)
          .pipe(filter((idle: boolean | undefined) => !!idle))
          .subscribe(() => this.showConfirmDialog())
      );
      this.subscribeToTimerStart();
      this.subscribeToTimerTimeOut();
    } else {
      this.dynamicLoaderService.removeAllDynamicComponents();
      this.store.dispatch(setUserIdle({ isUserIdle: false }));
    }
  }

  private subscribeToTimerStart(): void {
    this.idleSubscription.add(
      this.userIdle.onTimerStart().subscribe(count => {
        if (count === 1) {
          this.store.dispatch(setUserIdle({ isUserIdle: true }));
        }
        this.userSecondsIdle = count;
        console.log(this.userSecondsIdle);
        this.ref.markForCheck();
      })
    );
  }

  private subscribeToTimerTimeOut(): void {
    this.idleSubscription.add(
      this.userIdle.onTimeout().subscribe(() => {
        if (this.displayIdleConfirmDialog) {
          this.displayIdleConfirmDialog = false;
        }
        this.endIdleTimerAndSession();
      })
    );
  }

  private startWatching(): void {
    this.userIdle.startWatching();
  }

  private showConfirmDialog(): void {
    this.displayIdleConfirmDialog = true;
    this.ref.markForCheck();
  }

  private flushIdleTimer(): void {
    this.userIdle.stopWatching();
    this.displayIdleConfirmDialog = false;
    this.idleSubscription.unsubscribe();
    this.idleSubscription = new Subscription();
  }

  restartIdleTimer(): void {
    this.userIdle.stopWatching();
    this.displayIdleConfirmDialog = false;
    this.userIdle.startWatching();
    this.store.dispatch(setUserIdle({ isUserIdle: false }));
  }

  endIdleTimerAndSession(): void {
    this.flushIdleTimer();
    this.store.dispatch(setUserIdle({ isUserIdle: false }));
    this.dynamicLoaderService.removeAllDynamicComponents();
    this.authService.logout();
  }

  getUserShortName(): string | undefined {
    if (this.portalUser && this.portalUser.fullName) {
      // full name to be added in api
      return (
        this.portalUser.fullName
          .split(' ')
          .map(n => n[0])
          .join('') || 'DevAdmin'
      );
    }
    return undefined;
  }

  subscribeAuthenticatedUserChanged() {
    this.subscription.add(
      this.authService.userAuthenticationChanged$?.subscribe(user => {
        if (user) {
          this.portalUser = user;
          this.fullName = user?.fullName || '';
          this.isLoggedIn = true;
          this.getSysSecuredMenu();
          this._getExternalResourceExp();
        } else {
          // this.isLoggedIn = false;
        }
      })
    );
  }

  onSignOutClick(): void {
    this.authService.logout();
  }

  private subscribeToRouteChange(): void {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(event => {
        this.isHelp = event.url.includes('/help');
      });
  }

  onExternalResExpList() {
    this.popupHeading = this.translateService.instant('general.details');
    this.isExternalResExpPopup = true;
    this.ref.markForCheck();
  }

  closeEditMode() {
    this.isExternalResExpPopup = false;
    this.ref.markForCheck();
  }

  updateToLatest(): void {
    this.swUpdate.activateUpdate().then(() => document.location.reload());
  }
}
